import React, { useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Menu, Table, message } from "antd";
import { Bar } from "react-chartjs-2";
import {
  BarChart2,
  Calendar,
  ChevronDown,
  Download,
  DollarSign,
  Globe,
  RefreshCw,
  TrendingUp,
  Users,
} from "react-feather";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import Select from "react-select";
import DatePicker from "react-bootstrap-daterangepicker";
import moment from "moment";
import axios from "axios";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";

import exportService from "../../utils/exportService";
import PrintReportPreview from "../../reports/PrintReportPreview";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const apiURL = process.env.REACT_APP_API_URL || "";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const percent = (value) => `${Number(value || 0).toFixed(1)}%`;

const typeTone = {
  Direct: { background: "#ecfdf3", color: "#027a48", border: "#abefc6" },
  OTA: { background: "#f4f3ff", color: "#5925dc", border: "#d9d6fe" },
  Corporate: { background: "#eff8ff", color: "#175cd3", border: "#b2ddff" },
  Other: { background: "#f8fafc", color: "#475467", border: "#e4e7ec" },
};

const KpiCard = ({ label, value, sub, icon }) => (
  <div
    style={{
      background: "#ffffff",
      border: "1px solid #e4e7ec",
      borderRadius: 14,
      padding: "15px 16px",
      minHeight: 105,
      boxShadow: "0 3px 12px rgba(16, 24, 40, 0.035)",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            color: "#667085",
            fontSize: 10,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: ".07em",
          }}
        >
          {label}
        </div>
        <div
          style={{
            marginTop: 7,
            color: "#101828",
            fontSize: 22,
            fontWeight: 800,
            lineHeight: 1.15,
          }}
        >
          {value}
        </div>
        {sub && (
          <div style={{ marginTop: 6, color: "#98a2b3", fontSize: 10.5 }}>
            {sub}
          </div>
        )}
      </div>
      <div
        style={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: 10,
          display: "grid",
          placeItems: "center",
          background: "#f5f8ff",
          color: "#2f6fed",
        }}
      >
        {icon}
      </div>
    </div>
  </div>
);

const BookingSourcePerformance = () => {
  const businessDate = localStorage.getItem("business_date");
  const initialEnd = businessDate ? moment(businessDate) : moment();

  const [dateRange, setDateRange] = useState({
    startDate: initialEnd.clone().subtract(29, "days").startOf("day"),
    endDate: initialEnd.clone().endOf("day"),
  });
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({});
  const [byType, setByType] = useState({});
  const [loading, setLoading] = useState(false);
  const [sourceTypeFilter, setSourceTypeFilter] = useState(null);
  const [sourceFilter, setSourceFilter] = useState(null);
  const [pdfVisible, setPdfVisible] = useState(false);
  const [pdfData, setPdfData] = useState(null);

  const fetchReport = async () => {
    const token = localStorage.getItem("token");

    if (
      !dateRange.startDate ||
      !dateRange.endDate ||
      !moment(dateRange.startDate).isValid() ||
      !moment(dateRange.endDate).isValid()
    ) {
      message.error("Please select a valid date range.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${apiURL}/reports-daily/booking-source-performance`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            startDate: dateRange.startDate.format("YYYY-MM-DD"),
            endDate: dateRange.endDate.format("YYYY-MM-DD"),
          },
        }
      );

      if (!response.data?.success) {
        throw new Error(response.data?.message || "Unable to load report.");
      }

      setData(Array.isArray(response.data.sources) ? response.data.sources : []);
      setTotals(response.data.totals || {});
      setByType(response.data.by_type || {});
    } catch (error) {
      console.error("Booking source performance report error:", error);
      setData([]);
      setTotals({});
      setByType({});
      message.error(
        error?.response?.data?.message ||
          error.message ||
          "Unable to load booking source performance."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [dateRange.startDate?.format("YYYY-MM-DD"), dateRange.endDate?.format("YYYY-MM-DD")]);

  const sourceTypeOptions = [
    { value: "Direct", label: "Direct" },
    { value: "OTA", label: "OTA / Partner" },
    { value: "Corporate", label: "Corporate" },
    { value: "Other", label: "Other" },
  ];

  const sourceOptions = useMemo(
    () =>
      [...new Set(data.map((row) => row.source_name).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name })),
    [data]
  );

  const filteredData = useMemo(
    () =>
      data.filter((row) => {
        const typeMatch =
          !sourceTypeFilter || row.source_type === sourceTypeFilter.value;
        const sourceMatch =
          !sourceFilter || row.source_name === sourceFilter.value;
        return typeMatch && sourceMatch;
      }),
    [data, sourceTypeFilter, sourceFilter]
  );

  const filteredTotals = useMemo(() => {
    const totalBookings = filteredData.reduce(
      (sum, row) => sum + Number(row.bookings || 0),
      0
    );
    const cancelled = filteredData.reduce(
      (sum, row) => sum + Number(row.cancelled_bookings || 0),
      0
    );
    const roomNights = filteredData.reduce(
      (sum, row) => sum + Number(row.room_nights || 0),
      0
    );
    const revenue = filteredData.reduce(
      (sum, row) => sum + Number(row.room_revenue || 0),
      0
    );

    return {
      totalBookings,
      cancelled,
      roomNights,
      revenue,
      adr: roomNights > 0 ? revenue / roomNights : 0,
      cancellationRate:
        totalBookings > 0 ? (cancelled / totalBookings) * 100 : 0,
    };
  }, [filteredData]);

  const topRevenueSource = useMemo(
    () =>
      [...filteredData].sort(
        (a, b) => Number(b.room_revenue || 0) - Number(a.room_revenue || 0)
      )[0] || null,
    [filteredData]
  );

  const topBookingSource = useMemo(
    () =>
      [...filteredData].sort(
        (a, b) => Number(b.bookings || 0) - Number(a.bookings || 0)
      )[0] || null,
    [filteredData]
  );

  const chartRows = useMemo(
    () =>
      [...filteredData]
        .sort((a, b) => Number(b.bookings || 0) - Number(a.bookings || 0))
        .slice(0, 12),
    [filteredData]
  );

  const bookingChartData = {
    labels: chartRows.map((row) => row.source_name),
    datasets: [
      {
        label: "Bookings",
        data: chartRows.map((row) => Number(row.bookings || 0)),
        backgroundColor: "rgba(47, 111, 237, 0.78)",
        borderRadius: 5,
      },
    ],
  };

  const revenueChartData = {
    labels: chartRows.map((row) => row.source_name),
    datasets: [
      {
        label: "Room Revenue",
        data: chartRows.map((row) => Number(row.room_revenue || 0)),
        backgroundColor: "rgba(16, 185, 129, 0.72)",
        borderRadius: 5,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) =>
            context.dataset.label === "Room Revenue"
              ? ` ${money(context.raw)}`
              : ` ${Number(context.raw || 0).toLocaleString("en-IN")}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#667085", font: { size: 10 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#f0f2f5" },
        ticks: { color: "#98a2b3", font: { size: 10 } },
      },
    },
  };

  const columns = [
    {
      title: "Booking Source",
      dataIndex: "source_name",
      fixed: "left",
      width: 220,
      render: (value, row) => {
        const tone = typeTone[row.source_type] || typeTone.Other;
        return (
          <div>
            <div style={{ color: "#101828", fontSize: 12.5, fontWeight: 800 }}>
              {value || "Unknown"}
            </div>
            <span
              style={{
                display: "inline-flex",
                marginTop: 5,
                padding: "3px 7px",
                borderRadius: 999,
                background: tone.background,
                color: tone.color,
                border: `1px solid ${tone.border}`,
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: ".05em",
                textTransform: "uppercase",
              }}
            >
              {row.source_type}
            </span>
          </div>
        );
      },
    },
    {
      title: "Bookings",
      dataIndex: "bookings",
      width: 105,
      sorter: (a, b) => Number(a.bookings) - Number(b.bookings),
      render: (value) => Number(value || 0).toLocaleString("en-IN"),
    },
    {
      title: "Share",
      dataIndex: "booking_share",
      width: 95,
      sorter: (a, b) => Number(a.booking_share) - Number(b.booking_share),
      render: percent,
    },
    {
      title: "Room Nights",
      dataIndex: "room_nights",
      width: 115,
      sorter: (a, b) => Number(a.room_nights) - Number(b.room_nights),
      render: (value) => Number(value || 0).toLocaleString("en-IN"),
    },
    {
      title: "Room Revenue",
      dataIndex: "room_revenue",
      width: 140,
      sorter: (a, b) => Number(a.room_revenue) - Number(b.room_revenue),
      render: (value) => (
        <strong style={{ color: "#101828" }}>{money(value)}</strong>
      ),
    },
    {
      title: "ADR",
      dataIndex: "adr",
      width: 115,
      sorter: (a, b) => Number(a.adr) - Number(b.adr),
      render: money,
    },
    {
      title: "Avg Stay",
      dataIndex: "average_stay",
      width: 100,
      sorter: (a, b) => Number(a.average_stay) - Number(b.average_stay),
      render: (value) => `${Number(value || 0).toFixed(1)} nights`,
    },
    {
      title: "Cancelled",
      dataIndex: "cancelled_bookings",
      width: 100,
      sorter: (a, b) =>
        Number(a.cancelled_bookings) - Number(b.cancelled_bookings),
      render: (value) => Number(value || 0).toLocaleString("en-IN"),
    },
    {
      title: "Cancel %",
      dataIndex: "cancellation_rate",
      width: 105,
      sorter: (a, b) =>
        Number(a.cancellation_rate) - Number(b.cancellation_rate),
      render: (value) => {
        const number = Number(value || 0);
        return (
          <span style={{ color: number >= 10 ? "#b42318" : "#475467" }}>
            {percent(number)}
          </span>
        );
      },
    },
  ];

  const exportHeaders = [
    { key: "source_name", title: "Booking Source" },
    { key: "source_type", title: "Source Type" },
    { key: "bookings", title: "Bookings" },
    { key: "booking_share", title: "Booking Share" },
    { key: "room_nights", title: "Room Nights" },
    { key: "room_revenue", title: "Room Revenue" },
    { key: "adr", title: "ADR" },
    { key: "average_stay", title: "Average Stay" },
    { key: "cancelled_bookings", title: "Cancelled Bookings" },
    { key: "cancellation_rate", title: "Cancellation Rate" },
  ];

  const exportData = filteredData.map((row) => ({
    source_name: row.source_name,
    source_type: row.source_type,
    bookings: row.bookings,
    booking_share: percent(row.booking_share),
    room_nights: row.room_nights,
    room_revenue: Number(row.room_revenue || 0).toFixed(2),
    adr: Number(row.adr || 0).toFixed(2),
    average_stay: Number(row.average_stay || 0).toFixed(2),
    cancelled_bookings: row.cancelled_bookings,
    cancellation_rate: percent(row.cancellation_rate),
  }));

  const exportFooter = [
    { key: "Total Bookings", value: filteredTotals.totalBookings },
    { key: "Room Nights", value: filteredTotals.roomNights },
    { key: "Room Revenue", value: money(filteredTotals.revenue) },
    { key: "ADR", value: money(filteredTotals.adr) },
    {
      key: "Cancellation Rate",
      value: percent(filteredTotals.cancellationRate),
    },
  ];

  const suffix = `_${dateRange.startDate.format("YYYYMMDD")}_${dateRange.endDate.format(
    "YYYYMMDD"
  )}`;

  const handleExportCSV = () =>
    exportService.exportToCSV(
      exportData,
      exportHeaders,
      `booking_source_performance${suffix}.csv`
    );

  const handleExportXLS = () =>
    exportService.exportToXLS(
      exportData,
      exportHeaders,
      `booking_source_performance${suffix}.xlsx`,
      "Booking Source Performance"
    );

  const handleExportPDF = () =>
    exportService.exportToPDF(
      exportData,
      exportHeaders,
      "Booking Source Performance",
      `booking_source_performance${suffix}.pdf`,
      false,
      true,
      exportFooter,
      exportFooter
    );

  const handlePrint = async () => {
    try {
      setLoading(true);
      const pdfBlob = await exportService.exportToPDF(
        exportData,
        exportHeaders,
        "Booking Source Performance",
        null,
        true,
        true,
        exportFooter,
        exportFooter
      );
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfData(pdfUrl);
      setPdfVisible(true);
    } catch (error) {
      console.error(error);
      message.error("Unable to prepare print preview.");
    } finally {
      setLoading(false);
    }
  };

  const downloadMenu = (
    <Menu>
      <Menu.Item key="csv" onClick={handleExportCSV}>
        Download CSV
      </Menu.Item>
      <Menu.Item key="xls" onClick={handleExportXLS}>
        Download XLS
      </Menu.Item>
      <Menu.Item key="pdf" onClick={handleExportPDF}>
        Download PDF
      </Menu.Item>
      <Menu.Item key="print" onClick={handlePrint}>
        Print Preview
      </Menu.Item>
    </Menu>
  );

  const handleDateChange = (start, end) => {
    const nextStart = moment(start).startOf("day");
    const nextEnd = moment(end).endOf("day");
    if (!nextStart.isValid() || !nextEnd.isValid() || nextStart.isAfter(nextEnd)) {
      return;
    }

    setDateRange({ startDate: nextStart, endDate: nextEnd });
    setSourceTypeFilter(null);
    setSourceFilter(null);
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <style>{`
          .source-performance-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 12px;
          }
          .source-performance-charts {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
          }
          @media (max-width: 1200px) {
            .source-performance-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          }
          @media (max-width: 820px) {
            .source-performance-grid,
            .source-performance-charts { grid-template-columns: 1fr; }
          }
        `}</style>

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "#2f6fed",
                fontSize: 10,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: ".10em",
              }}
            >
              <Globe size={14} />
              Acquisition intelligence
            </div>
            <h3
              style={{
                margin: "6px 0 4px",
                color: "#101828",
                fontSize: 24,
                fontWeight: 800,
              }}
            >
              Booking Source Performance
            </h3>
            <div style={{ color: "#667085", fontSize: 12.5 }}>
              Compare OTA, direct, social, referral and corporate sources by
              bookings, room revenue, ADR and cancellations.
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Button
              onClick={fetchReport}
              loading={loading}
              icon={<RefreshCw size={14} />}
              style={{ height: 38, borderRadius: 9, fontWeight: 700 }}
            >
              Refresh
            </Button>
            <Dropdown overlay={downloadMenu} trigger={["click"]}>
              <Button
                icon={<Download size={14} />}
                style={{ height: 38, borderRadius: 9, fontWeight: 700 }}
              >
                Export <ChevronDown size={13} style={{ marginLeft: 5 }} />
              </Button>
            </Dropdown>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(280px, 1.25fr) minmax(180px, .65fr) minmax(200px, .75fr)",
            gap: 10,
            alignItems: "center",
            padding: 12,
            marginBottom: 14,
            background: "#ffffff",
            border: "1px solid #e4e7ec",
            borderRadius: 13,
          }}
        >
          <DatePicker
            initialSettings={{
              startDate: dateRange.startDate.toDate(),
              endDate: dateRange.endDate.toDate(),
              ranges: {
                Today: [moment().startOf("day").toDate(), moment().endOf("day").toDate()],
                "Last 7 Days": [
                  moment().subtract(6, "days").startOf("day").toDate(),
                  moment().endOf("day").toDate(),
                ],
                "Last 30 Days": [
                  moment().subtract(29, "days").startOf("day").toDate(),
                  moment().endOf("day").toDate(),
                ],
                "This Month": [
                  moment().startOf("month").toDate(),
                  moment().endOf("month").toDate(),
                ],
                "Last Month": [
                  moment().subtract(1, "month").startOf("month").toDate(),
                  moment().subtract(1, "month").endOf("month").toDate(),
                ],
              },
              locale: { format: "DD MMM YYYY", applyLabel: "Apply" },
            }}
            onCallback={handleDateChange}
          >
            <button
              type="button"
              style={{
                width: "100%",
                height: 40,
                display: "flex",
                alignItems: "center",
                gap: 9,
                padding: "0 12px",
                border: "1px solid #d0d5dd",
                borderRadius: 9,
                background: "#ffffff",
                color: "#344054",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <Calendar size={14} color="#667085" />
              {dateRange.startDate.format("DD MMM YYYY")} –{" "}
              {dateRange.endDate.format("DD MMM YYYY")}
            </button>
          </DatePicker>

          <Select
            isClearable
            value={sourceTypeFilter}
            onChange={setSourceTypeFilter}
            options={sourceTypeOptions}
            placeholder="Source type"
            styles={{
              control: (base) => ({
                ...base,
                minHeight: 40,
                borderColor: "#d0d5dd",
                borderRadius: 9,
                boxShadow: "none",
                fontSize: 12,
              }),
            }}
          />

          <Select
            isClearable
            value={sourceFilter}
            onChange={setSourceFilter}
            options={sourceOptions}
            placeholder="Specific source"
            styles={{
              control: (base) => ({
                ...base,
                minHeight: 40,
                borderColor: "#d0d5dd",
                borderRadius: 9,
                boxShadow: "none",
                fontSize: 12,
              }),
            }}
          />
        </div>

        <div className="source-performance-grid" style={{ marginBottom: 14 }}>
          <KpiCard
            label="Total Bookings"
            value={filteredTotals.totalBookings.toLocaleString("en-IN")}
            sub={`${filteredData.length} active source${filteredData.length === 1 ? "" : "s"}`}
            icon={<Users size={17} />}
          />
          <KpiCard
            label="Room Revenue"
            value={money(filteredTotals.revenue)}
            sub="Cancelled bookings excluded"
            icon={<DollarSign size={17} />}
          />
          <KpiCard
            label="ADR"
            value={money(filteredTotals.adr)}
            sub={`${filteredTotals.roomNights.toLocaleString("en-IN")} room nights`}
            icon={<TrendingUp size={17} />}
          />
          <KpiCard
            label="Cancellation Rate"
            value={percent(filteredTotals.cancellationRate)}
            sub={`${filteredTotals.cancelled.toLocaleString("en-IN")} cancelled bookings`}
            icon={<BarChart2 size={17} />}
          />

          <KpiCard
            label="Direct Booking Share"
            value={percent(byType?.direct?.booking_share)}
            sub={`${Number(byType?.direct?.bookings || 0).toLocaleString("en-IN")} direct bookings`}
            icon={<Globe size={17} />}
          />
          <KpiCard
            label="Direct Revenue Share"
            value={percent(byType?.direct?.revenue_share)}
            sub={money(byType?.direct?.room_revenue)}
            icon={<DollarSign size={17} />}
          />
          <KpiCard
            label="Top Source by Revenue"
            value={topRevenueSource?.source_name || "—"}
            sub={topRevenueSource ? money(topRevenueSource.room_revenue) : "No data"}
            icon={<TrendingUp size={17} />}
          />
          <KpiCard
            label="Top Source by Bookings"
            value={topBookingSource?.source_name || "—"}
            sub={
              topBookingSource
                ? `${Number(topBookingSource.bookings || 0).toLocaleString("en-IN")} bookings`
                : "No data"
            }
            icon={<Users size={17} />}
          />
        </div>

        <div className="source-performance-charts" style={{ marginBottom: 14 }}>
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e4e7ec",
              borderRadius: 14,
              padding: 15,
            }}
          >
            <div style={{ color: "#101828", fontSize: 13, fontWeight: 800 }}>
              Bookings by Source
            </div>
            <div style={{ color: "#98a2b3", fontSize: 10.5, marginTop: 3 }}>
              Top sources within the selected date range
            </div>
            <div style={{ height: 265, marginTop: 12 }}>
              <Bar data={bookingChartData} options={chartOptions} />
            </div>
          </div>

          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e4e7ec",
              borderRadius: 14,
              padding: 15,
            }}
          >
            <div style={{ color: "#101828", fontSize: 13, fontWeight: 800 }}>
              Room Revenue by Source
            </div>
            <div style={{ color: "#98a2b3", fontSize: 10.5, marginTop: 3 }}>
              Net room revenue attributed to each booking source
            </div>
            <div style={{ height: 265, marginTop: 12 }}>
              <Bar data={revenueChartData} options={chartOptions} />
            </div>
          </div>
        </div>

        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e4e7ec",
            borderRadius: 14,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "13px 15px",
              borderBottom: "1px solid #eef2f6",
            }}
          >
            <div style={{ color: "#101828", fontSize: 13, fontWeight: 800 }}>
              Source Performance Detail
            </div>
            <div style={{ color: "#98a2b3", fontSize: 10.5, marginTop: 3 }}>
              Booking counts use check-in date. Revenue excludes cancelled bookings.
            </div>
          </div>
          <Table
            rowKey={(row) => `${row.source_type}-${row.source_name}`}
            loading={loading}
            dataSource={filteredData}
            columns={columns}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (count) => `${count} booking sources`,
            }}
            scroll={{ x: 1180 }}
            size="middle"
          />
        </div>

        {pdfVisible && (
          <PrintReportPreview
            visible={pdfVisible}
            pdfData={pdfData}
            onClose={() => {
              setPdfVisible(false);
              if (pdfData) URL.revokeObjectURL(pdfData);
              setPdfData(null);
            }}
            onDownloadPDF={handleExportPDF}
          />
        )}
      </div>
    </div>
  );
};

export default BookingSourcePerformance;
